import { useEffect, useMemo, useState } from 'react'
import { usePools, useVvsVault } from 'state/pools/hooks'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'
import tokens from 'config/constants/tokens'
import { getPoolApr } from 'utils/apr'
import { getBalanceNumber } from 'utils/formatBalance'
import usePriceXvvsUsdc from 'views/xVvs/hooks/usePriceXvvsUsdc'
import { getAprData } from 'views/Pools/helpers'

type TopGlitterMine = {
  primarySrc: string
  secondarySrc: string
  name: string
  percent: number
  type: 'APR' | 'APY'
  symbol: string
}

export const useGetTopGlitterMine = () => {
  const { pools: poolsWithoutAutoVault, userDataLoaded } = usePools()
  const [topMine, setTopMine] = useState<TopGlitterMine>(null)
  const xvvsPastWeekApr = usePastWeekApr()
  const xvvsPriceUsd = usePriceXvvsUsdc()
  const vvsVault = useVvsVault()
  useEffect(() => {
    // values like past week APR might be slightly changing each moment, but recalculating
    // is not necessary so often
    if (topMine || !xvvsPastWeekApr || !xvvsPriceUsd || !userDataLoaded) {
      return
    }

    // similar to views/Pools/index.tsx
    const getTopPool = () => {
      const vvsPool = poolsWithoutAutoVault.find((pool) => pool.sousId === 0)
      const vvsAutoVault = { ...vvsPool, isAutoVault: true }
      const rawPools = [vvsAutoVault, ...poolsWithoutAutoVault]
      const pools = rawPools.map((pool) => {
        if (pool.stakingToken.address !== tokens.xvvs.address) {
          return pool
        }
        // Process pools that uses xVVS as staked tokens
        const minesApr = getPoolApr(
          xvvsPriceUsd.toNumber(),
          pool.earningTokenPrice,
          getBalanceNumber(pool.totalStaked, pool.stakingToken.decimals),
          parseFloat(pool.tokenPerBlock),
        )
        return {
          ...pool,
          stakingTokenPrice: xvvsPriceUsd.toNumber(),
          apr: !pool.isFinished ? minesApr + xvvsPastWeekApr : 0,
          xvvsPastWeekApr,
          minesApr,
        }
      })

      return pools.reduce((prev, current) => (prev.apr > current.apr ? prev : current))
    }
    const topPool = getTopPool()
    const primarySrc = topPool.stakingToken.address
    const secondarySrc = topPool.sousId === 0 ? 'autorenew' : topPool.earningToken.address
    const type = topPool.sousId === 0 ? 'APY' : 'APR'
    const name = topPool.sousId === 0 ? 'Auto VVS' : `Earn ${topPool.earningToken.name}`
    const { performanceFee } = vvsVault.fees
    const symbol = topPool.sousId === 0 ? 'Auto VVS' : `Earn ${topPool.earningToken.symbol}`
    const performanceFeeAsDecimal = performanceFee && performanceFee / 100
    const percent =
      topPool.sousId === 0 ? getAprData({ ...topPool, isAutoVault: true }, performanceFeeAsDecimal).apr : topPool.apr
    setTopMine({ primarySrc, secondarySrc, type, name, percent, symbol })
  }, [poolsWithoutAutoVault, xvvsPastWeekApr, xvvsPriceUsd, vvsVault, topMine, userDataLoaded])
  return topMine
}
